import { useNavigate } from '@tanstack/react-router';

import type { ApprovalRequestMessage } from '../../../../../../types/ApprovalRequestMessage';
import { ApprovalRequestState } from '../../../../../../types/ApprovalRequestState';
import { Button } from '../../../../../cdl/Button/Button';
import { Text } from '../../../../../cdl/Text/Text';
import { translate } from '../../../../../common/helpers/translate.helper';
import { useRole } from '../../../../../common/hooks/useRole';
import { Box } from '../../../../../common/ui/Box';
import { OfferState } from '../../../../../offer/model/OfferState';
import { OfferActionsGrid } from '../../../common/OfferCard/OfferActionsGrid';
import { ApprovalRequestMessageDisplay } from '../../../components/OfferCard/components/ApprovalRequestMessageDisplay';
import { OfferStatus } from '../../../components/OfferCard/components/OfferStatus';
import { OfferStatusDisplay } from '../../../components/OfferCard/components/OfferStatusDisplay';
import { useOfferCardContext } from '../../../components/OfferCard/hooks/useOfferCardContext';
import { hasMeaningfulDeclineReason } from '../../helpers/hasMeaningfulDeclineReason';

const renderApprovalRequestState = (approvalRequest: ApprovalRequestMessage) => {
    const { state } = approvalRequest;
    const responseMessage = 'responseMessage' in approvalRequest ? approvalRequest.responseMessage : undefined;

    if (state === ApprovalRequestState.DECLINED || state === ApprovalRequestState.APPROVED) {
        return <OfferStatusDisplay additionalMessage={<ApprovalRequestMessageDisplay message={responseMessage} />} />;
    }

    if (state === ApprovalRequestState.WITHDRAWN) {
        return (
            <OfferStatusDisplay
                additionalMessage={
                    <Text variant="action" color="foreground.muted">
                        {translate('order.detail.offerCard.withdrawnMessage')}
                    </Text>
                }
            />
        );
    }

    return <OfferStatusDisplay />;
};

export const OfferActions = () => {
    const { offer } = useOfferCardContext();
    const navigate = useNavigate();
    const role = useRole();

    if (offer.wasQuoted() && offer.isUnquoted() && !offer.isCanceled()) {
        return <OfferStatusDisplay showActions={!offer.isSpot()} />;
    }

    if (offer.isUnquoted() && !offer.isCanceled() && !offer.approvalRequests.length) {
        return <OfferStatusDisplay />;
    }

    if (offer.approvalRequests.length && !offer.isOrder() && !offer.isCanceled()) {
        return renderApprovalRequestState(offer.approvalRequests[0]);
    }

    if (offer.isQuotedEnquiry() && !offer.isCanceled()) {
        return <OfferStatusDisplay />;
    }

    if (offer.isCanceled()) {
        const showDeclineReason = offer.state === OfferState.ENQUIRY_DECLINED && hasMeaningfulDeclineReason(offer);

        return (
            <OfferActionsGrid>
                <Box display="flex" flexDirection="column" gap={12}>
                    <OfferStatus />
                    {showDeclineReason ? (
                        <Text variant="extraSmall" color="foreground.muted" fontStyle="italic">
                            {`"${offer.cancelReason}"`}
                        </Text>
                    ) : null}
                </Box>

                {role.isSuperAdmin() ? (
                    <Button width="100%" onClick={() => navigate({ to: '/offer/$id', params: { id: offer.id } })}>
                        {translate('order.detail.offerCard.viewOfferDetails')}
                    </Button>
                ) : null}
            </OfferActionsGrid>
        );
    }

    return (
        <OfferActionsGrid>
            <OfferStatus />

            <Button
                emphasis="high"
                width="100%"
                onClick={() => navigate({ to: '/offer/$id', params: { id: offer.id } })}
            >
                {translate('order.detail.offerCard.viewOrder')}
            </Button>
        </OfferActionsGrid>
    );
};
